import type { ApiErrorBody, LoginResponse } from './types';

const BASE = '/api/v1';
const ACCESS_KEY = 'crm.access_token';
const REFRESH_KEY = 'crm.refresh_token';

export class ApiError extends Error {
  constructor(
    readonly body: ApiErrorBody['error'],
    readonly status: number,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }
  get code() {
    return this.body.code;
  }
  /** Field-level messages keyed by input name, ready to attach to a form. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.body.details ?? []).map((d) => [d.field, d.message]));
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const sessionExpiredListeners = new Set<() => void>();
export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

// A single in-flight refresh shared by every request that 401s at once.
// Without it, ten parallel queries fire ten refreshes and nine race to
// overwrite the token.
let refreshing: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  refreshing ??= (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) throw new Error('no refresh token');

    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) {
      tokens.clear();
      sessionExpiredListeners.forEach((l) => l());
      throw new ApiError(
        { code: 'session_expired', message: 'Your session has expired', requestId: '' },
        401,
      );
    }
    const data = (await res.json()) as LoginResponse;
    tokens.set(data.access_token, data.refresh_token);
  })().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  anonymous?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}, retry = true): Promise<T> {
  const { body, anonymous, headers, ...rest } = options;
  const access = tokens.access;

  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(!anonymous && access ? { Authorization: `Bearer ${access}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // One retry after refreshing; a second 401 means the session is really gone.
  if (res.status === 401 && retry && !anonymous && tokens.refresh) {
    await refreshSession();
    return api<T>(path, options, false);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const errorBody = (parsed as ApiErrorBody | null)?.error ?? {
      code: 'unknown_error',
      message: `Request failed with status ${res.status}`,
      requestId: '',
    };
    throw new ApiError(errorBody, res.status);
  }
  return parsed as T;
}

/* ------------------------------ query building ------------------------------ */

export type CustomFieldFilterValue =
  | string
  | string[]
  | Partial<Record<'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains', string | number>>;

export interface LeadQuery {
  limit?: number;
  cursor?: string | null;
  search?: string;
  status?: string[];
  /** Lead source ids. */
  source?: string[];
  assigned_to?: string[];
  /** Campaign ids. */
  campaign?: string[];
  unassigned?: boolean;
  created_from?: string;
  created_to?: string;
  assigned_from?: string;
  assigned_to_date?: string;
  date_field?: 'created' | 'assigned';
  include_duplicates?: boolean;
  include_estimate?: boolean;
  include_count?: boolean;
  cf?: Record<string, CustomFieldFilterValue>;
}

/**
 * URLSearchParams cannot express the nested `cf[key][op]` form the API uses for
 * custom-field filters, so those are appended by hand.
 */
export function buildLeadQuery(query: LeadQuery): string {
  const p = new URLSearchParams();

  if (query.limit) p.set('limit', String(query.limit));
  if (query.cursor) p.set('cursor', query.cursor);
  if (query.search?.trim()) p.set('search', query.search.trim());
  if (query.status?.length) p.set('status', query.status.join(','));
  if (query.source?.length) p.set('source', query.source.join(','));
  if (query.assigned_to?.length) p.set('assigned_to', query.assigned_to.join(','));
  if (query.campaign?.length) p.set('campaign', query.campaign.join(','));
  if (query.unassigned) p.set('unassigned', 'true');
  if (query.created_from) p.set('created_from', query.created_from);
  if (query.created_to) p.set('created_to', query.created_to);
  if (query.assigned_from) p.set('assigned_from', query.assigned_from);
  if (query.assigned_to_date) p.set('assigned_to_date', query.assigned_to_date);
  if (query.date_field) p.set('date_field', query.date_field);
  if (query.include_duplicates) p.set('include_duplicates', 'true');
  if (query.include_estimate) p.set('include_estimate', 'true');
  if (query.include_count) p.set('include_count', 'true');

  for (const [key, value] of Object.entries(query.cf ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => p.append(`cf[${key}][]`, v));
    } else if (typeof value === 'object') {
      for (const [op, operand] of Object.entries(value)) {
        if (operand === undefined || operand === '') continue;
        p.append(`cf[${key}][${op}]`, String(operand));
      }
    } else {
      p.append(`cf[${key}]`, String(value));
    }
  }

  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/* ------------------------------ change password ------------------------------ */
// Forgot password is the emailed-code flow (pages/ForgotPasswordPage.tsx); the
// phone-OTP helpers that briefly lived here had no backend and were removed in
// the merge of 45ba7da.

/**
 * Changes the signed-in user's password. The server signs out every other
 * session and returns fresh tokens for this one, which are stored here so the
 * person stays signed in.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await api<LoginResponse>('/auth/change-password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  });
  tokens.set(res.access_token, res.refresh_token);
}


/**
 * Opens a performance report PDF in a new tab. The PDF needs the bearer token,
 * so it is fetched here and handed to the tab as a blob URL. The tab is opened
 * first, synchronously, so popup blockers treat it as the user's own click.
 */
export async function openReportPreview(params: { user_id: string; from: string; to: string; note?: string }): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    await api('/auth/me'); // refreshes the access token first if it has expired
    const qs = new URLSearchParams({ user_id: params.user_id, from: params.from, to: params.to });
    if (params.note) qs.set('note', params.note);
    const res = await fetch(`${BASE}/reports/performance/preview?${qs}`, {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
      throw new ApiError(body?.error ?? { code: 'unknown_error', message: `Preview failed (${res.status})`, requestId: '' }, res.status);
    }
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
